import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { hqoTheme } from '@hqo/react-components-library/dist/hqo-theme-provider';
import { selectBrandTheme } from 'store/theme/selectors';
import { lighten, darken } from 'polished';

// Defensive fallback: some buildings don't have a custom font configured (brandTheme.font is
// missing/null), and in production hqoTheme.fonts has occasionally resolved to undefined
// (library version drift), which then throws downstream in StyledButton's `theme.fonts.join()`
// (RES-727). Always guarantee a real, non-empty font stack regardless of what the library exports.
const FALLBACK_FONTS = ['Roboto', 'Helvetica Neue', 'Sans-Serif'];

export const getSafeFonts = (fonts?: string[]): string[] => (fonts && fonts.length ? fonts : FALLBACK_FONTS);

export const useGetTheme = () => {
  const brandTheme = useSelector(selectBrandTheme);

  return useMemo(() => {
    if (brandTheme) {
      const primaryColor = brandTheme.primary1 ? brandTheme.primary1 : brandTheme.primary_color;
      const secondaryColor = brandTheme.primary2 ? brandTheme.primary2 : brandTheme.secondary_color;

      return {
        ...hqoTheme,
        colors: {
          ...hqoTheme.colors,
          $primary: primaryColor || hqoTheme.colors.$primary,
          $secondary: secondaryColor || hqoTheme.colors.$secondary,
          primary: primaryColor || hqoTheme.colors.primary,
          secondary: secondaryColor || hqoTheme.colors.secondary,
          hqo: {
            ...hqoTheme.colors.hqo,
            primary: primaryColor || hqoTheme.colors.hqo.primary,
            secondary: secondaryColor || hqoTheme.colors.hqo.secondary,
            primaryHover: primaryColor ? lighten(0.1, primaryColor) : hqoTheme.colors.hqo.primaryHover,
            primaryActive: primaryColor ? darken(0.1, primaryColor) : hqoTheme.colors.hqo.primaryActive,
            secondaryHover: secondaryColor ? lighten(0.1, secondaryColor) : hqoTheme.colors.hqo.secondaryHover,
            secondaryActive: secondaryColor ? darken(0.1, secondaryColor) : hqoTheme.colors.hqo.secondaryActive,
          },
          fontColor: brandTheme.primary_font_color ? brandTheme.primary_font_color : hqoTheme.colors.fontColor,
        },
        fonts: brandTheme.font?.body?.font_family
          ? [`${brandTheme.font?.body?.font_family}`, ...getSafeFonts(hqoTheme.fonts)]
          : getSafeFonts(hqoTheme.fonts),
      };
    }
    return { ...hqoTheme, fonts: getSafeFonts(hqoTheme.fonts) };
  }, [brandTheme]);
};
